import type { DefuseProgress } from '../types'
import type { MineFieldGraph } from './MineFieldGraph'
import { emptyChannelDurationSec } from './generateLayout'

type Idle = { kind: 'idle' }
type Channeling = {
  kind: 'channeling'
  targetSlotId: string
  elapsedSec: number
  durationSec: number
}

type State = Idle | Channeling

export type DefuseTickResult =
  | { kind: 'idle' }
  | { kind: 'progress'; slotId: string; t: number }
  | { kind: 'complete'; slotId: string; result: 'defused' | 'empty' }
  | { kind: 'cancelled' }

/**
 * Aim-based hold channel: Idle ↔ Channeling.
 * Cancel при отпускании / смене aim / потере range.
 */
export class DefuseSM {
  private state: State = { kind: 'idle' }

  reset(): void {
    this.state = { kind: 'idle' }
  }

  get progress(): DefuseProgress {
    if (this.state.kind !== 'channeling') {
      return { slotId: null, t: 0, active: false }
    }
    const t =
      this.state.durationSec > 0
        ? Math.min(1, this.state.elapsedSec / this.state.durationSec)
        : 1
    return { slotId: this.state.targetSlotId, t, active: true }
  }

  tick(
    dt: number,
    hold: boolean,
    aimSlot: string | null,
    graph: MineFieldGraph,
  ): DefuseTickResult {
    if (!hold || !aimSlot || !graph.canBeginDefuse(aimSlot)) {
      if (this.state.kind === 'channeling') {
        this.state = { kind: 'idle' }
        return { kind: 'cancelled' }
      }
      return { kind: 'idle' }
    }

    // Смена цели — сброс прогресса
    if (this.state.kind === 'channeling' && this.state.targetSlotId !== aimSlot) {
      this.state = { kind: 'idle' }
    }

    if (this.state.kind === 'idle') {
      const durationSec = graph.channelDurationFor(aimSlot, emptyChannelDurationSec())
      this.state = {
        kind: 'channeling',
        targetSlotId: aimSlot,
        elapsedSec: 0,
        durationSec,
      }
    }

    const ch = this.state as Channeling
    ch.elapsedSec += dt

    if (ch.elapsedSec >= ch.durationSec) {
      const slotId = ch.targetSlotId
      this.state = { kind: 'idle' }
      const result = graph.completeDefuse(slotId)
      if (result === 'defused' || result === 'empty') {
        return { kind: 'complete', slotId, result }
      }
      return { kind: 'cancelled' }
    }

    return {
      kind: 'progress',
      slotId: ch.targetSlotId,
      t: ch.elapsedSec / ch.durationSec,
    }
  }
}
